import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { reportSchedulesAPI } from '../lib/api';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import { Palette, Loader2, CheckCircle2 } from 'lucide-react';

const DEFAULTS = {
    header_logo: 'none',
    watermark: 'none',
    watermark_style: 'center',
    watermark_opacity: 0.08,
    cover_page: false,
    custom_logo_data: null,
    logo_size: 100,
    sender_contact: { name: '', phone: '', email: '' },
};

const legacy = (v) => (v === 'agenta' ? 'yulontap' : v);

export const normalizeBranding = (raw = {}) => ({
    header_logo: legacy(raw.header_logo) || 'none',
    watermark: legacy(raw.watermark) || 'none',
    watermark_style: raw.watermark_style || 'center',
    watermark_opacity: raw.watermark_opacity || 0.08,
    cover_page: !!raw.cover_page,
    custom_logo_data: raw.custom_logo_data || null,
    logo_size: Math.min(Math.max(Number(raw.logo_size) || 100, 50), 200),
    sender_contact: {
        name: raw.sender_contact?.name || '',
        phone: raw.sender_contact?.phone || '',
        email: raw.sender_contact?.email || '',
    },
});

/**
 * PDF branding editor. Two modes:
 * - Self-persisting (default, Sales Report page): pass `selectedOrg`; loads
 *   and auto-saves the restaurant's report-level defaults (600ms debounce).
 * - Controlled (Schedule modal): pass `value` + `onChange` (+ optional
 *   `loading`); no fetching or saving — the parent owns persistence.
 */
const PdfBrandingSettings = ({ selectedOrg, value, onChange, loading = false }) => {
    const { t } = useTranslation();
    const controlled = typeof onChange === 'function';
    const [inner, setInner] = useState(DEFAULTS);
    const [innerLoaded, setInnerLoaded] = useState(false);
    const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
    const saveTimer = useRef(null);

    const b = controlled ? normalizeBranding(value || {}) : inner;
    const loaded = controlled ? !loading : innerLoaded;

    useEffect(() => {
        if (controlled) return undefined;
        setInnerLoaded(false);
        setSaveState('idle');
        clearTimeout(saveTimer.current);
        reportSchedulesAPI.getBranding(selectedOrg || null)
            .then(res => setInner(normalizeBranding(res.data?.branding || {})))
            .catch(() => setInner(DEFAULTS))
            .finally(() => setInnerLoaded(true));
        return () => clearTimeout(saveTimer.current);
    }, [selectedOrg, controlled]);

    const needsCustomLogo = (next) => next.header_logo === 'custom' || next.watermark === 'custom';

    const update = (patch) => {
        const next = { ...b, ...patch };
        if (controlled) {
            onChange(next);
            return;
        }
        setInner(next);
        if (needsCustomLogo(next) && !next.custom_logo_data) return; // wait for upload
        clearTimeout(saveTimer.current);
        setSaveState('saving');
        saveTimer.current = setTimeout(async () => {
            try {
                await reportSchedulesAPI.putBranding(selectedOrg || null, {
                    ...next,
                    custom_logo_data: needsCustomLogo(next) ? next.custom_logo_data : null,
                });
                setSaveState('saved');
            } catch (error) {
                setSaveState('error');
                toast.error(error.response?.data?.detail || t('salesReport.branding.saveFailed'));
            }
        }, 600);
    };

    const onLogoFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!['image/png', 'image/jpeg'].includes(file.type)) {
            toast.error(t('salesReport.schedule.branding.logoInvalid'));
            return;
        }
        if (file.size > 700 * 1024) {
            toast.error(t('salesReport.schedule.branding.logoTooLarge'));
            return;
        }
        const reader = new FileReader();
        reader.onload = () => update({ custom_logo_data: reader.result });
        reader.readAsDataURL(file);
    };

    return (
        <div className="border rounded-lg p-4 space-y-4" data-testid="branding-section" data-insight-id="branding-section">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-sm">{t('salesReport.schedule.branding.title')}</span>
                </div>
                <span className="text-xs text-muted-foreground flex items-center gap-2" data-testid="branding-save-status">
                    {!controlled && saveState === 'saving' && <><Loader2 className="w-3 h-3 animate-spin" />{t('salesReport.branding.saving')}</>}
                    {!controlled && saveState === 'saved' && <><CheckCircle2 className="w-3 h-3 text-green-600" />{t('salesReport.branding.saved')}</>}
                    <button
                        type="button"
                        onClick={() => update({ ...DEFAULTS })}
                        className="underline-offset-2 hover:underline text-muted-foreground/80"
                        data-testid="branding-clear-btn"
                    >
                        {t('salesReport.branding.clear')}
                    </button>
                </span>
            </div>
            {!controlled && (
                <p className="text-xs text-muted-foreground -mt-2">{t('salesReport.branding.desc')}</p>
            )}
            {!loaded ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                </div>
            ) : (
                <>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="mb-2 block">{t('salesReport.schedule.branding.headerLogo')}</Label>
                            <Select value={b.header_logo} onValueChange={(v) => update({ header_logo: v })}>
                                <SelectTrigger data-testid="branding-header-logo-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">{t('salesReport.schedule.branding.logoNone')}</SelectItem>
                                    <SelectItem value="yulontap">{t('salesReport.schedule.branding.logoYulontap')}</SelectItem>
                                    <SelectItem value="restaurant">{t('salesReport.schedule.branding.logoRestaurant')}</SelectItem>
                                    <SelectItem value="custom">{t('salesReport.schedule.branding.logoCustom')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="mb-2 block">{t('salesReport.schedule.branding.watermark')}</Label>
                            <Select value={b.watermark} onValueChange={(v) => update({ watermark: v })}>
                                <SelectTrigger data-testid="branding-watermark-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">{t('salesReport.schedule.branding.logoNone')}</SelectItem>
                                    <SelectItem value="same">{t('salesReport.schedule.branding.wmSame')}</SelectItem>
                                    <SelectItem value="yulontap">{t('salesReport.schedule.branding.logoYulontap')}</SelectItem>
                                    <SelectItem value="restaurant">{t('salesReport.schedule.branding.logoRestaurant')}</SelectItem>
                                    <SelectItem value="custom">{t('salesReport.schedule.branding.logoCustom')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    {b.header_logo !== 'none' && (
                        <div>
                            <Label className="mb-2 block">
                                {t('salesReport.branding.logoSize')}: {Math.round(b.logo_size || 100)}%
                            </Label>
                            <Slider
                                value={[Math.round(b.logo_size || 100)]}
                                onValueChange={([v]) => update({ logo_size: v })}
                                min={50}
                                max={200}
                                step={5}
                                className="mt-3 max-w-md"
                                data-testid="branding-logo-size-slider"
                            />
                            <p className="text-xs text-muted-foreground mt-2">{t('salesReport.branding.logoSizeHint')}</p>
                        </div>
                    )}
                    {b.watermark !== 'none' && (
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="mb-2 block">{t('salesReport.schedule.branding.watermarkStyle')}</Label>
                                <Select value={b.watermark_style} onValueChange={(v) => update({ watermark_style: v })}>
                                    <SelectTrigger data-testid="branding-watermark-style-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="center">{t('salesReport.schedule.branding.styleCenter')}</SelectItem>
                                        <SelectItem value="vertical">{t('salesReport.schedule.branding.styleVertical')}</SelectItem>
                                        <SelectItem value="tiled">{t('salesReport.schedule.branding.styleTiled')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="mb-2 block">
                                    {t('salesReport.schedule.branding.opacity')}: {Math.round(b.watermark_opacity * 100)}%
                                </Label>
                                <Slider
                                    value={[Math.round(b.watermark_opacity * 100)]}
                                    onValueChange={([v]) => update({ watermark_opacity: v / 100 })}
                                    min={2}
                                    max={30}
                                    step={1}
                                    className="mt-3"
                                    data-testid="branding-opacity-slider"
                                />
                            </div>
                        </div>
                    )}
                    {needsCustomLogo(b) && (
                        <div>
                            <Label className="mb-2 block">{t('salesReport.schedule.branding.uploadLogo')}</Label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg"
                                    onChange={onLogoFile}
                                    className="text-sm"
                                    data-testid="branding-custom-logo-input"
                                />
                                {b.custom_logo_data && (
                                    <img src={b.custom_logo_data} alt="logo" className="h-10 max-w-[120px] object-contain border rounded bg-white p-1" data-testid="branding-custom-logo-preview" />
                                )}
                            </div>
                            {!b.custom_logo_data && (
                                <p className="text-xs text-amber-600 mt-1">{t('salesReport.schedule.branding.logoMissing')}</p>
                            )}
                        </div>
                    )}
                    {(b.header_logo === 'restaurant' || b.watermark === 'restaurant') && (
                        <p className="text-xs text-muted-foreground">{t('salesReport.schedule.branding.restaurantLogoHint')}</p>
                    )}
                    <div className="border-t pt-3">
                        <Label className="block">{t('salesReport.schedule.branding.contactTitle')}</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">{t('salesReport.schedule.branding.contactHint')}</p>
                        <div className="grid sm:grid-cols-3 gap-3 mt-3">
                            {['name', 'phone', 'email'].map((field) => (
                                <div key={field}>
                                    <Label className="mb-1.5 block text-xs text-muted-foreground">
                                        {t(`salesReport.schedule.branding.contact${field.charAt(0).toUpperCase()}${field.slice(1)}`)}
                                    </Label>
                                    <Input
                                        value={b.sender_contact?.[field] || ''}
                                        maxLength={120}
                                        onChange={(e) => update({ sender_contact: { ...(b.sender_contact || {}), [field]: e.target.value } })}
                                        data-testid={`branding-contact-${field}-input`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center justify-between pt-1">
                        <div>
                            <Label className="block">{t('salesReport.schedule.branding.coverPage')}</Label>
                            <p className="text-xs text-muted-foreground mt-0.5">{t('salesReport.schedule.branding.coverPageHint')}</p>
                        </div>
                        <Switch checked={b.cover_page} onCheckedChange={(v) => update({ cover_page: v })} data-testid="branding-cover-page-switch" />
                    </div>
                </>
            )}
        </div>
    );
};

export default PdfBrandingSettings;
